import mergeFetchedWithLocalStreamingMessages from './mergeFetchedWithLocalStreamingMessages';

export const setupAssistantInitialState = {
  conversationSlug: null,
  messages: [],
  loading: false,
  sending: false,
  streaming: false,
  retryingMessageId: null,
  activeTool: null,
  error: null,
  blocked: false,
  forbidden: false,
  draft: null,
  draftLoading: false,
  draftError: null,
  progressPending: null,
  progressError: null,
  restarting: false,
  conversationResetFailed: false,
  applyPending: false,
  applyOutcome: null,
  replyPreviews: {},
  streamActivityAt: null,
  pendingSeenAt: {},
};

export const ADMIN_REQUIRED_MESSAGE = 'Ask an admin to set up Referral Management.';
export const SETUP_NOT_ENABLED_MESSAGE = 'The setup assistant isn\'t enabled for your organization yet.';

export const SETUP_FAILURE_MESSAGES = {
  referral_setup_unavailable: 'Setup assistant is no longer available for your organization',
  tool_call_limit: 'That took too many steps, try a smaller change',
  reply_stalled: 'This reply stopped responding. Start a new chat to keep going. Your setup draft stays as it is.',
};

/**
 * Longer than any silence a healthy reply shows while the `ai_chat_llm_global`
 * limiter reschedules it: each attempt broadcasts `ai_chat.stream.reset`, and
 * the wait before the next attempt is up to 300s, 600s and 900s on the first,
 * second and third reschedule.
 */
export const STALLED_REPLY_MS = 20 * 60 * 1000;

export const CONVERSATION_REPLACED_MESSAGE = 'This chat was started over in another tab.';
export const RESET_AFTER_RESTART_MESSAGE = 'Started over, but the conversation couldn\'t be reset.';
export const DRAFT_UNAVAILABLE_MESSAGE = 'Setup isn\'t available yet.';
export const PROGRESS_FALLBACK_MESSAGE = 'That didn\'t work. Try again.';

export const PROGRESS_ERROR_MESSAGES = {
  section_locked: 'Confirm statuses first to unlock this section.',
  statuses_need_confirmation: 'Confirm statuses on the Statuses card first.',
  sections_incomplete: 'Finish or skip every other section before completing the review.',
  section_not_optional: 'This section can\'t be skipped.',
  section_not_skipped: 'This section isn\'t skipped anymore.',
};

export const DRAFT_RESTARTED_MESSAGE = 'Setup was started over. This is the current draft.';
export const APPLY_FALLBACK_MESSAGE = 'Could not apply the setup. Try again.';
export const APPLY_CHANGED_MESSAGE = 'The setup changed. Review it again before you apply.';
export const ALREADY_APPLIED_MESSAGE = 'This setup was already applied.';
const APPLY_NOTICES = [APPLY_CHANGED_MESSAGE, ALREADY_APPLIED_MESSAGE];

const sameId = (a, b) => String(a) === String(b);
const isOptimisticAssistant = (message) => String(message.id).startsWith('optimistic-assistant-');
const isTerminal = (message) => message?.status === 'completed' || message?.status === 'failed';
const withoutOptimisticAssistants = (messages) => messages.filter((message) => !isOptimisticAssistant(message));
const isSectionIntro = (message) => message?.metadata?.kind === 'section_intro';
const isPendingAssistant = (message) => message.role === 'assistant' && message.status === 'pending';
const hasPendingAssistant = (messages) => messages.some(isPendingAssistant);
const seePending = (seenAt, ids, now) => {
  const unseen = ids.filter((id) => !(String(id) in seenAt));
  return unseen.length ? { ...seenAt, ...Object.fromEntries(unseen.map((id) => [String(id), now])) } : seenAt;
};
const withStalledReplies = (messages, seenAt, streamActivityAt, now) => messages.map((message) => {
  if (!isPendingAssistant(message)) return message;
  const lastActivity = Math.max(seenAt[String(message.id)] ?? now, streamActivityAt || 0);
  return now - lastActivity > STALLED_REPLY_MS ? { ...message, status: 'failed', error_code: 'reply_stalled' } : message;
});

/**
 * True for a reply this page marked `failed` with `error_code`
 * `reply_stalled`; the server still holds it as pending.
 */
export const isStalledReply = (message) => message?.status === 'failed' && message.error_code === 'reply_stalled';

const errorFrom = (payload, fallback) => payload?.response?.data?.error || payload?.error || fallback;
const isForbidden = (payload) => payload?.response?.status === 403;
const failureState = (payload, fallback) => (
  isForbidden(payload)
    ? { blocked: true, forbidden: true, error: null }
    : { forbidden: false, error: errorFrom(payload, fallback) }
);

const withReplyPreview = (state, previewKey, preview) => ({
  ...state,
  replyPreviews: { ...state.replyPreviews, [previewKey]: preview },
});

const progressFailure = (payload, section) => {
  const code = payload?.response?.data?.code || null;
  return {
    code,
    message: PROGRESS_ERROR_MESSAGES[code] || errorFrom(payload, PROGRESS_FALLBACK_MESSAGE),
    section: section ?? null,
  };
};

/**
 * `applyOutcome` for a refused confirmation or apply other than a 409. A 422
 * with Apply's `errors` keeps only the errors with a `row_id` as `failed`
 * `errors`, with `message` set to `APPLY_FALLBACK_MESSAGE` when errors
 * without a row came with them; with no row errors it is `error` with
 * `APPLY_FALLBACK_MESSAGE`, so no apply-level server text is shown. Anything
 * else is `error` with the server's message.
 */
const applyFailure = (payload) => {
  const status = payload?.response?.status;
  const errors = payload?.response?.data?.referral_setup_apply?.errors;
  if (status !== 422 || !Array.isArray(errors) || errors.length === 0) {
    return { status: 'error', message: errorFrom(payload, APPLY_FALLBACK_MESSAGE) };
  }
  const rowErrors = errors.filter((error) => error.row_id);
  if (rowErrors.length === 0) return { status: 'error', message: APPLY_FALLBACK_MESSAGE };
  return {
    status: 'failed',
    errors: rowErrors,
    message: rowErrors.length < errors.length ? APPLY_FALLBACK_MESSAGE : null,
  };
};

const appliedOutcome = (result) => {
  if (result?.applied) return { status: 'applied', summary: result.summary || null };
  return { status: 'error', message: APPLY_FALLBACK_MESSAGE };
};

const withoutApplyNotice = (state) => (APPLY_NOTICES.includes(state.error) ? { ...state, error: null } : state);

const draftLoadError = (payload) => (
  payload?.response?.status === 404
    ? DRAFT_UNAVAILABLE_MESSAGE
    : errorFrom(payload, 'Could not load the setup draft.')
);

/**
 * Picks the draft to keep when `incoming` arrives from a GET, PATCH, POST or
 * `ai_chat.draft.updated` event. A non-object `incoming` is ignored. Draft ids
 * grow with each restart, so a draft with a higher `id` (a restart) replaces
 * the current one and a lower `id` (a late event from a discarded draft) is
 * ignored. For the same `id`, `incoming` wins unless its `updated_at` is older
 * than `current`'s.
 */
export const newerDraft = (current, incoming) => {
  if (!incoming || typeof incoming !== 'object' || Array.isArray(incoming)) return current;
  if (!current) return incoming;
  if (incoming.id !== current.id) return incoming.id > current.id ? incoming : current;
  return Date.parse(incoming.updated_at) >= Date.parse(current.updated_at) ? incoming : current;
};

const predatesPendingFocus = (state, incoming) => (
  state.progressPending?.progressAction === 'focus'
  && incoming?.id === state.draft?.id
  && Date.parse(incoming?.updated_at) === Date.parse(state.draft?.updated_at)
);

const adoptDraft = (state, incoming) => {
  if (predatesPendingFocus(state, incoming)) return state;
  const draft = newerDraft(state.draft, incoming);
  if (draft === state.draft) return state;
  const focusChanged = draft.progress?.focus !== state.draft?.progress?.focus;
  return { ...state, draft, progressError: focusChanged ? null : state.progressError };
};

const withFocus = (draft, section, sectionState) => ({
  ...draft,
  progress: {
    ...draft.progress,
    focus: section,
    sections: { ...draft.progress?.sections, [section]: sectionState },
  },
});

const revertFocus = (draft, meta) => {
  if (meta.progressAction !== 'focus' || !draft) return draft;
  if (meta.draftId && draft.id !== meta.draftId) return draft;
  if (draft.progress?.focus !== meta.section) return draft;
  const { sections } = draft.progress;
  return {
    ...draft,
    progress: {
      ...draft.progress,
      focus: meta.previousFocus ?? draft.progress.focus,
      sections: { ...sections, [meta.section]: meta.previousSectionState ?? sections[meta.section] },
    },
  };
};

const progressErrorSection = (meta) => (
  meta.progressAction === 'confirm_statuses' ? 'statuses' : meta.section
);

const upsert = (messages, nextMessage) => {
  const index = messages.findIndex((message) => sameId(message.id, nextMessage.id));
  if (index === -1) return [...messages, nextMessage];
  const next = [...messages];
  next[index] = { ...next[index], ...nextMessage };
  return next;
};

const placeAssistant = (messages, assistant) => {
  if (messages.some((message) => sameId(message.id, assistant.id))) {
    return upsert(withoutOptimisticAssistants(messages), assistant);
  }
  const index = messages.findIndex(isOptimisticAssistant);
  if (index === -1) return [...messages, assistant];
  return withoutOptimisticAssistants(messages.map((message, at) => (at === index ? assistant : message)));
};

const pendingAssistant = (id, content = '') => ({
  id,
  role: 'assistant',
  status: 'pending',
  content,
  created_at: new Date().toISOString(),
});

const isCurrent = (state, conversationSlug) => (
  Boolean(conversationSlug) && conversationSlug === state.conversationSlug
);

const SETUP_ACTION_TYPES = new Set([
  'BEGIN_GET_REFERRAL_SETUP_CONVERSATIONS',
  'FINISHED_GET_REFERRAL_SETUP_CONVERSATIONS',
  'FAILED_GET_REFERRAL_SETUP_CONVERSATIONS',
  'BEGIN_CREATE_REFERRAL_SETUP_CONVERSATION',
  'FINISHED_CREATE_REFERRAL_SETUP_CONVERSATION',
  'FAILED_CREATE_REFERRAL_SETUP_CONVERSATION',
  'SELECT_REFERRAL_SETUP_CONVERSATION',
  'BEGIN_GET_REFERRAL_SETUP_MESSAGES',
  'FINISHED_GET_REFERRAL_SETUP_MESSAGES',
  'FAILED_GET_REFERRAL_SETUP_MESSAGES',
  'BEGIN_SEND_REFERRAL_SETUP_MESSAGE',
  'FINISHED_SEND_REFERRAL_SETUP_MESSAGE',
  'FAILED_SEND_REFERRAL_SETUP_MESSAGE',
  'BEGIN_RETRY_REFERRAL_SETUP_MESSAGE',
  'FINISHED_RETRY_REFERRAL_SETUP_MESSAGE',
  'FAILED_RETRY_REFERRAL_SETUP_MESSAGE',
  'REFERRAL_SETUP_STREAM_DELTA',
  'REFERRAL_SETUP_STREAM_RESET',
  'REFERRAL_SETUP_STREAM_COMPLETED',
  'REFERRAL_SETUP_STREAM_FAILED',
  'REFERRAL_SETUP_STREAM_TOOL',
  'REFERRAL_SETUP_MESSAGE_CREATED',
  'REFERRAL_SETUP_DRAFT_UPDATED',
  'REFERRAL_SETUP_CONVERSATION_REPLACED',
  'BEGIN_RESET_REFERRAL_SETUP_CONVERSATION',
  'FINISHED_RESET_REFERRAL_SETUP_CONVERSATION',
  'FAILED_RESET_REFERRAL_SETUP_CONVERSATION',
  'BEGIN_GET_REFERRAL_SETUP_DRAFT',
  'FINISHED_GET_REFERRAL_SETUP_DRAFT',
  'FAILED_GET_REFERRAL_SETUP_DRAFT',
  'BEGIN_UPDATE_REFERRAL_SETUP_PROGRESS',
  'FINISHED_UPDATE_REFERRAL_SETUP_PROGRESS',
  'FAILED_UPDATE_REFERRAL_SETUP_PROGRESS',
  'BEGIN_RESTART_REFERRAL_SETUP_DRAFT',
  'FINISHED_RESTART_REFERRAL_SETUP_DRAFT',
  'FAILED_RESTART_REFERRAL_SETUP_DRAFT',
  'BEGIN_GET_REFERRAL_SETUP_REPLY_PREVIEW',
  'FINISHED_GET_REFERRAL_SETUP_REPLY_PREVIEW',
  'FAILED_GET_REFERRAL_SETUP_REPLY_PREVIEW',
  'BEGIN_CONFIRM_REFERRAL_SETUP_APPLY',
  'FINISHED_CONFIRM_REFERRAL_SETUP_APPLY',
  'FAILED_CONFIRM_REFERRAL_SETUP_APPLY',
  'BEGIN_APPLY_REFERRAL_SETUP_DRAFT',
  'FINISHED_APPLY_REFERRAL_SETUP_DRAFT',
  'FAILED_APPLY_REFERRAL_SETUP_DRAFT',
  'RESET_REFERRAL_SETUP_PAGE',
]);

export const isSetupAssistantAction = (action) => SETUP_ACTION_TYPES.has(action?.type);

/**
 * Error text for the setup conversation. A 403 (`forbidden`) reads differently for
 * an org admin, whose org lacks a required feature, than for anyone else.
 */
export const setupErrorMessage = ({ forbidden, error }, isAdmin) => {
  if (!forbidden) return error;
  return isAdmin ? SETUP_NOT_ENABLED_MESSAGE : ADMIN_REQUIRED_MESSAGE;
};

/**
 * `applyOutcome` while `draft` is the draft the Apply was made against
 * (`applyOutcome.draftId`), otherwise null, so a result never shows over a
 * draft that a reload or Start over adopted since.
 */
export const currentApplyOutcome = ({ applyOutcome, draft }) => (
  applyOutcome && draft && applyOutcome.draftId === draft.id ? applyOutcome : null
);

/**
 * `setupAssistant` sub-state of the aiChat slice. Stream actions for any
 * conversation other than `conversationSlug` are ignored. `blocked` is set by a
 * 403 (non-admin or setup flag off) or a `referral_setup_unavailable` failure and
 * means the conversation cannot take new messages; the next load clears it.
 * `forbidden` marks the 403 case, whose copy comes from `setupErrorMessage`.
 * A `tool_call_limit` failure stays retryable and is described on the message.
 * A messages reload marks a reply `failed` with `error_code` `reply_stalled`
 * when the server still reports it pending more than `STALLED_REPLY_MS` after
 * the later of `pendingSeenAt[id]` and `streamActivityAt`, both on this
 * page's clock: `pendingSeenAt` records when this page first saw each pending
 * reply id (a send or retry acknowledgement, which restarts it, a reload or
 * a stream event), and `streamActivityAt` the last send, retry or stream
 * event. The server's `created_at` is never compared, so a skewed client
 * clock cannot stall a live reply. That ends `streaming`, so Start over is
 * enabled again; the server refuses to retry or send past a pending reply.
 * A stream delta, reset or completion for a stalled reply takes it back.
 * A retry acknowledgement that arrives after a stream reset or terminal event
 * already adopted the retry is ignored.
 * A message the server stores on its own (`ai_chat.message.created`, such as a
 * section intro) is appended once, by id, after every message already shown;
 * a streamed reply that arrives later takes the place of its optimistic
 * placeholder, so the transcript keeps the server's order. A messages reload
 * that predates a section intro already shown keeps that intro.
 *
 * `draft` is the organization's `ReferralSetupDraft#object`, replaced only
 * through `newerDraft`. A `focus` progress change moves `draft.progress.focus`
 * before the server answers and is undone on failure if focus has not moved
 * since; until it settles, a draft with the same `id` and `updated_at` as
 * `draft` is ignored, so a load started before it cannot move focus back.
 * Other progress changes wait for the server. `progressPending` is the
 * `{ progressAction, section }` in flight. `progressError` is
 * `{ code, message, section }` from the last refused change; it clears on the
 * next progress change and whenever focus moves. A `draft_restarted` refusal
 * sets `error` to `DRAFT_RESTARTED_MESSAGE` instead, because the reload that
 * follows usually moves focus. A finished conversation reset
 * returns to the initial state, keeping `draft`. A conversation replaced after
 * the server lost it sets `error` to `CONVERSATION_REPLACED_MESSAGE`.
 * `conversationResetFailed` marks a
 * restart whose draft was replaced but whose conversation reset failed; only the
 * conversation reset should be retried. Leaving the page returns the whole
 * sub-state to its initial state.
 *
 * `applyPending` is true from the Apply confirmation request until the apply
 * request settles, or until the confirmation fails. `applyOutcome` is null
 * until then, and then `{ status: 'applied', summary }` with the adopted
 * applied draft, `{ status: 'failed', errors, message }` with Apply's
 * `{ code, row_id, message }` row errors (see `applyFailure`), or
 * `{ status: 'error', message }`, each with the `draftId` the Apply was made
 * against (`meta.draftId`); read it through `currentApplyOutcome`. It clears
 * when the next Apply starts. A 409
 * from either request sets `error` to `APPLY_CHANGED_MESSAGE` and an
 * `already_applied` answer sets it to `ALREADY_APPLIED_MESSAGE` instead, as
 * for `draft_restarted`, because the reload that follows can adopt another
 * draft and move focus; either clears when the next Apply, progress change or
 * Start over begins. A 403 blocks setup instead.
 */
const referralSetupAssistant = (state = setupAssistantInitialState, action = {}) => {
  const { payload, meta } = action;

  switch (action.type) {
    case 'BEGIN_GET_REFERRAL_SETUP_CONVERSATIONS':
    case 'BEGIN_CREATE_REFERRAL_SETUP_CONVERSATION':
      return {
        ...state, loading: true, error: null, blocked: false, forbidden: false,
      };
    case 'FAILED_GET_REFERRAL_SETUP_CONVERSATIONS':
    case 'FAILED_CREATE_REFERRAL_SETUP_CONVERSATION':
      return { ...state, loading: false, ...failureState(payload, 'Could not start the setup assistant.') };
    case 'SELECT_REFERRAL_SETUP_CONVERSATION': {
      const { conversation } = payload;
      if (conversation.slug === state.conversationSlug) return state;
      return {
        ...state,
        conversationSlug: conversation.slug,
        messages: [],
        streaming: false,
        activeTool: null,
        pendingSeenAt: {},
      };
    }
    case 'BEGIN_GET_REFERRAL_SETUP_MESSAGES':
      return {
        ...state, loading: true, error: null, blocked: false, forbidden: false,
      };
    case 'FINISHED_GET_REFERRAL_SETUP_MESSAGES': {
      if (!isCurrent(state, meta?.conversationSlug)) return { ...state, loading: false };
      const now = Date.now();
      const merged = mergeFetchedWithLocalStreamingMessages(state.messages, payload.data?.messages || [], isSectionIntro);
      const pendingIds = merged.flatMap((message) => (isPendingAssistant(message) ? [message.id] : []));
      const pendingSeenAt = seePending(state.pendingSeenAt, pendingIds, now);
      const messages = withStalledReplies(merged, pendingSeenAt, state.streamActivityAt, now);
      const streaming = hasPendingAssistant(messages);
      return {
        ...state,
        loading: false,
        messages,
        streaming,
        activeTool: streaming ? state.activeTool : null,
        pendingSeenAt,
      };
    }
    case 'FAILED_GET_REFERRAL_SETUP_MESSAGES':
      return { ...state, loading: false, ...failureState(payload, 'Could not load the conversation.') };
    case 'BEGIN_SEND_REFERRAL_SETUP_MESSAGE':
      if (!isCurrent(state, meta?.conversationSlug)) return state;
      return {
        ...state,
        sending: true,
        error: null,
        messages: [...state.messages, meta.optimisticUserMessage, meta.optimisticAssistantMessage],
      };
    case 'FINISHED_SEND_REFERRAL_SETUP_MESSAGE': {
      if (!isCurrent(state, meta?.conversationSlug)) return { ...state, sending: false };
      const userMessage = payload.data?.user_message;
      const streamId = payload.data?.stream?.assistant_message_id;
      let messages = state.messages.map((message) => (
        userMessage && sameId(message.id, meta.optimisticUserMessage.id) ? userMessage : message
      ));
      if (streamId) {
        const existing = messages.find((message) => sameId(message.id, streamId));
        messages = existing ? withoutOptimisticAssistants(messages) : placeAssistant(messages, pendingAssistant(streamId));
      }
      return {
        ...state,
        sending: false,
        streaming: hasPendingAssistant(messages),
        messages,
        streamActivityAt: Date.now(),
        pendingSeenAt: streamId ? seePending(state.pendingSeenAt, [streamId], Date.now()) : state.pendingSeenAt,
      };
    }
    case 'FAILED_SEND_REFERRAL_SETUP_MESSAGE': {
      if (!isCurrent(state, meta?.conversationSlug)) return { ...state, sending: false };
      const optimisticIds = [meta.optimisticUserMessage.id, meta.optimisticAssistantMessage.id];
      return {
        ...state,
        sending: false,
        messages: state.messages.filter((message) => !optimisticIds.some((id) => sameId(id, message.id))),
        ...failureState(payload, 'Your message was not sent. Try again.'),
      };
    }
    case 'BEGIN_RETRY_REFERRAL_SETUP_MESSAGE':
      return { ...state, retryingMessageId: meta?.failedMessageId ?? null, error: null };
    case 'FINISHED_RETRY_REFERRAL_SETUP_MESSAGE': {
      if (state.retryingMessageId === null) return state;
      const streamId = payload.data?.stream?.assistant_message_id || meta?.failedMessageId;
      if (!isCurrent(state, meta?.conversationSlug) || !streamId) {
        return { ...state, retryingMessageId: null };
      }
      return {
        ...state,
        retryingMessageId: null,
        streaming: true,
        messages: upsert(state.messages, { id: streamId, status: 'pending', content: '' }),
        streamActivityAt: Date.now(),
        pendingSeenAt: { ...state.pendingSeenAt, [String(streamId)]: Date.now() },
      };
    }
    case 'FAILED_RETRY_REFERRAL_SETUP_MESSAGE':
      return {
        ...state,
        retryingMessageId: null,
        ...failureState(payload, 'Retry failed. Try again.'),
      };
    case 'REFERRAL_SETUP_STREAM_DELTA': {
      const { conversationSlug, assistantMessageId, delta } = payload;
      if (!isCurrent(state, conversationSlug) || !assistantMessageId) return state;
      const existing = state.messages.find((message) => sameId(message.id, assistantMessageId));
      if (isTerminal(existing) && !isStalledReply(existing)) return state;
      const revived = isStalledReply(existing) ? { status: 'pending', error_code: null } : {};
      const messages = existing
        ? upsert(state.messages, { id: assistantMessageId, content: `${existing.content || ''}${delta || ''}`, ...revived })
        : placeAssistant(state.messages, pendingAssistant(assistantMessageId, delta || ''));
      return {
        ...state,
        streaming: true,
        messages,
        streamActivityAt: Date.now(),
        pendingSeenAt: seePending(state.pendingSeenAt, [assistantMessageId], Date.now()),
      };
    }
    case 'REFERRAL_SETUP_STREAM_RESET': {
      const { conversationSlug, assistantMessageId } = payload;
      if (!isCurrent(state, conversationSlug) || !assistantMessageId) return state;
      return {
        ...state,
        streaming: true,
        retryingMessageId: sameId(state.retryingMessageId, assistantMessageId) ? null : state.retryingMessageId,
        messages: placeAssistant(
          state.messages,
          { ...pendingAssistant(assistantMessageId), status: 'pending', content: '' },
        ),
        streamActivityAt: Date.now(),
        pendingSeenAt: seePending(state.pendingSeenAt, [assistantMessageId], Date.now()),
      };
    }
    case 'REFERRAL_SETUP_STREAM_COMPLETED':
      if (!isCurrent(state, payload.conversationSlug) || !payload.assistantMessage) return state;
      return {
        ...state,
        streaming: false,
        activeTool: null,
        retryingMessageId: sameId(state.retryingMessageId, payload.assistantMessage.id) ? null : state.retryingMessageId,
        messages: placeAssistant(state.messages, payload.assistantMessage),
        error: null,
      };
    case 'REFERRAL_SETUP_STREAM_FAILED': {
      const { conversationSlug, assistantMessage, error } = payload;
      if (!isCurrent(state, conversationSlug) || !assistantMessage) return state;
      const code = assistantMessage.error_code;
      const next = {
        ...state,
        streaming: false,
        activeTool: null,
        retryingMessageId: sameId(state.retryingMessageId, assistantMessage.id) ? null : state.retryingMessageId,
        messages: placeAssistant(state.messages, assistantMessage),
      };
      if (code === 'referral_setup_unavailable') {
        return { ...next, blocked: true, error: SETUP_FAILURE_MESSAGES[code] };
      }
      if (code === 'tool_call_limit') return { ...next, error: null };
      return { ...next, error: error || state.error };
    }
    case 'REFERRAL_SETUP_STREAM_TOOL': {
      const {
        conversationSlug, tool, status, label,
      } = payload;
      if (!isCurrent(state, conversationSlug)) return state;
      return {
        ...state,
        activeTool: status === 'started' ? { tool, label } : null,
        streamActivityAt: Date.now(),
      };
    }
    case 'REFERRAL_SETUP_MESSAGE_CREATED': {
      const { conversationSlug, message } = payload;
      if (!isCurrent(state, conversationSlug) || !message?.id) return state;
      if (state.messages.some((existing) => sameId(existing.id, message.id))) return state;
      return { ...state, messages: [...state.messages, message] };
    }
    case 'REFERRAL_SETUP_DRAFT_UPDATED':
      return adoptDraft(state, payload?.draft);
    case 'REFERRAL_SETUP_CONVERSATION_REPLACED':
      return { ...state, error: CONVERSATION_REPLACED_MESSAGE };
    case 'BEGIN_GET_REFERRAL_SETUP_DRAFT':
      return { ...state, draftLoading: true, draftError: null };
    case 'FINISHED_GET_REFERRAL_SETUP_DRAFT':
      return { ...adoptDraft(state, payload?.data?.referral_setup_draft), draftLoading: false };
    case 'FAILED_GET_REFERRAL_SETUP_DRAFT':
      if (isForbidden(payload)) return { ...state, draftLoading: false, ...failureState(payload) };
      return { ...state, draftLoading: false, draftError: draftLoadError(payload) };
    case 'BEGIN_UPDATE_REFERRAL_SETUP_PROGRESS': {
      const next = {
        ...withoutApplyNotice(state),
        progressPending: { progressAction: meta.progressAction, section: meta.section ?? null },
        progressError: null,
      };
      if (meta.progressAction !== 'focus' || !state.draft) return next;
      const current = state.draft.progress?.sections?.[meta.section];
      return {
        ...next,
        draft: withFocus(state.draft, meta.section, current === 'not_started' ? 'in_progress' : current),
      };
    }
    case 'FINISHED_UPDATE_REFERRAL_SETUP_PROGRESS':
      return adoptDraft({ ...state, progressPending: null }, payload?.data?.referral_setup_draft);
    case 'FAILED_UPDATE_REFERRAL_SETUP_PROGRESS': {
      const settled = { ...state, progressPending: null, draft: revertFocus(state.draft, meta) };
      if (isForbidden(payload)) return { ...settled, ...failureState(payload) };
      if (payload?.response?.data?.code === 'draft_restarted') return { ...settled, error: DRAFT_RESTARTED_MESSAGE };
      return { ...settled, progressError: progressFailure(payload, progressErrorSection(meta)) };
    }
    case 'BEGIN_RESTART_REFERRAL_SETUP_DRAFT':
      return {
        ...state, restarting: true, error: null, progressError: null,
      };
    case 'FINISHED_RESTART_REFERRAL_SETUP_DRAFT':
      return { ...adoptDraft(state, payload?.data?.referral_setup_draft), restarting: false };
    case 'FAILED_RESTART_REFERRAL_SETUP_DRAFT':
      return { ...state, restarting: false, ...failureState(payload, 'Could not start over. Try again.') };
    case 'BEGIN_GET_REFERRAL_SETUP_REPLY_PREVIEW':
      return withReplyPreview(state, meta.previewKey, { status: 'loading', blob: null });
    case 'FINISHED_GET_REFERRAL_SETUP_REPLY_PREVIEW':
      return withReplyPreview(state, meta.previewKey, { status: 'ready', blob: payload?.data ?? null });
    case 'FAILED_GET_REFERRAL_SETUP_REPLY_PREVIEW':
      return withReplyPreview(state, meta.previewKey, { status: 'error', blob: null });
    case 'BEGIN_CONFIRM_REFERRAL_SETUP_APPLY':
      return { ...withoutApplyNotice(state), applyPending: true, applyOutcome: null };
    case 'FAILED_CONFIRM_REFERRAL_SETUP_APPLY':
    case 'FAILED_APPLY_REFERRAL_SETUP_DRAFT':
      if (isForbidden(payload)) return { ...state, applyPending: false, ...failureState(payload) };
      if (payload?.response?.status === 409) return { ...state, applyPending: false, error: APPLY_CHANGED_MESSAGE };
      return { ...state, applyPending: false, applyOutcome: { ...applyFailure(payload), draftId: meta?.draftId ?? null } };
    case 'FINISHED_APPLY_REFERRAL_SETUP_DRAFT': {
      const result = payload?.data?.referral_setup_apply;
      if (result?.already_applied) return { ...state, applyPending: false, error: ALREADY_APPLIED_MESSAGE };
      const next = { ...state, applyPending: false, applyOutcome: { ...appliedOutcome(result), draftId: meta?.draftId ?? null } };
      return result?.applied ? adoptDraft(next, result.referral_setup_draft) : next;
    }
    case 'RESET_REFERRAL_SETUP_PAGE':
      return setupAssistantInitialState;
    case 'BEGIN_RESET_REFERRAL_SETUP_CONVERSATION':
      return {
        ...state, restarting: true, error: null, conversationResetFailed: false,
      };
    case 'FINISHED_RESET_REFERRAL_SETUP_CONVERSATION':
      return { ...setupAssistantInitialState, draft: state.draft, replyPreviews: state.replyPreviews };
    case 'FAILED_RESET_REFERRAL_SETUP_CONVERSATION':
      if (isForbidden(payload)) return { ...state, restarting: false, ...failureState(payload) };
      return {
        ...state, restarting: false, conversationResetFailed: true, error: RESET_AFTER_RESTART_MESSAGE,
      };
    default:
      return state;
  }
};

export default referralSetupAssistant;
